import * as React from 'react'
import cx from 'classnames'
import styles from './SplitViewHeader.sass'
import Icon from 'components/Icon'
import { FrameValue } from 'react-spring'
import ScrollShadow from '../ScrollShadow'
import { useFallbackNumberSpring } from 'hooks/spring-hooks'
import { clamp } from '../../utils/math-util'

interface SplitViewHeaderProps {
  children?: React.ReactNode
  compact?: boolean
  scrollOffset?: number | FrameValue<number>
  /**
   * If specified, renders a progress bar at the bottom border.
   */
  progress?: number
}

const DELTA = 64

/**
 " Split View Header
 */
export function SplitViewHeader({
  children,
  compact,
  scrollOffset,
  progress,
}: SplitViewHeaderProps) {
  const shadowOpacity = useFallbackNumberSpring(scrollOffset).to((v) =>
    clamp(v / DELTA)
  )
  return (
    <div className={cx(styles.root, compact && styles.compact)}>
      <div className={styles.content}>{children}</div>
      {scrollOffset !== undefined && (
        // We lift it up (z-index) because the scrolling
        // happens in a sibling container and not a parent, and
        // this element isn't sticky.
        <ScrollShadow lift borderless opacity={shadowOpacity} />
      )}
      {progress !== undefined && (
        <div
          className={styles.progress}
          style={{ transform: `scaleX(${clamp(progress / 100)})` }}
        />
      )}
    </div>
  )
}

interface SplitViewHeaderSearchProps {
  placeholder?: string
  value: string
  onChange: (newValue: string) => unknown
  withoutSearchIcon?: boolean
}

/**
 " Split View Search Header
 */
export function SplitViewHeaderSearch({
  placeholder = 'Search',
  onChange,
  value,
  withoutSearchIcon = false,
}: SplitViewHeaderSearchProps) {
  const searchFieldRef = React.useRef<HTMLInputElement>(null)
  const onSearchBarClick = React.useCallback(
    () => searchFieldRef.current && searchFieldRef.current.focus(),
    []
  )
  return (
    <div className={styles.searchBar} onClick={onSearchBarClick}>
      {!withoutSearchIcon && (
        <div className={styles.searchIcon}>
          <Icon name="search" />
        </div>
      )}
      <input
        ref={searchFieldRef}
        type="text"
        placeholder={placeholder}
        className={cx(styles.searchField, !value && styles.empty)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

interface SplitViewHeaderTitleProps {
  children?: React.ReactNode
  title?: string
  ['data-test']?: string
}

/**
 " Split View Header Title
 */
export function SplitViewHeaderTitle({
  children,
  title,
  'data-test': dataTest,
}: SplitViewHeaderTitleProps) {
  return (
    <div className={styles.title} title={title} data-test={dataTest}>
      {children}
    </div>
  )
}

interface SplitViewHeaderSubtitleProps {
  children?: React.ReactNode
}

/**
 " Split View Header Subtitle
 */
export function SplitViewHeaderSubtitle({
  children,
}: SplitViewHeaderSubtitleProps) {
  return <div className={styles.subtitle}>{children}</div>
}
